import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  configureScope as configureSentryScope,
  setUser as setSentryUser,
} from "@sentry/react";
import LogRocket from "logrocket";

import { BillingStoreActions, useBillingStore } from "stores/BillingStore";
import { useOrganizationStore } from "stores/OrganizationStore";
import { useUserStore } from "stores/UserStore";

import asyncOrSwim from "utility/async_or_swim";
import { authenticatedGetFetch } from "utility/authenticated_fetch";
import { captureSentryExceptionUnless } from "utility/capture_sentry_exception_unless";
import { getAccessToken, getRefreshToken } from "utility/credential_storage";

// -----------------------------------------------------------------------------

const ErrorPageRegex = /^\/misc(\/error)?/;

// -----------------------------------------------------------------------------

export function useRequiredData() {
  const navigate = useNavigate();

  const [, userActions] = useUserStore();
  const [, organizationActions] = useOrganizationStore();
  const [, billingActions] = useBillingStore();

  const [ready, setReady] = useState(false);

  //
  useEffect(() => {
    if (ready) {
      return;
    }

    //
    function logout() {
      userActions.clear();
      isNotAnErrorPage() && navigate("/login");
      setReady(true);
    }

    //
    asyncOrSwim(
      async () => {
        if (!getAccessToken()) {
          return logout();
        }

        //
        const user = await loadUser();
        userActions.set(user);

        const [organization] = await Promise.all([
          loadOrganization(user.organization),
          loadBilling(billingActions),
        ]);
        organizationActions.set(user.organization, organization);

        //
        setReady(true);
      },
      (error) => {
        console.error(error);
        logout();
      }
    );
  }, [ready, userActions, organizationActions, billingActions, navigate]);

  //
  return ready;
}

// -----------------------------------------------------------------------------

async function loadUser() {
  try {
    const user = await authenticatedGetFetch("api/me", {
      dontRetry: getRefreshToken() === "",
    });

    //
    LogRocket.identify(user.uuid, {
      name: user.full_name,
      email: user.email,
    });

    LogRocket.getSessionURL(function (sessionURL) {
      configureSentryScope(function (scope) {
        scope.setExtra("sessionURL", sessionURL);
      });
    });

    //
    setSentryUser({
      email: user.email,
      username: user.full_name,
    });

    //
    const [first, ...last] = user.full_name.split(" ");
    window.ap3c.track?.({
      v: 0,
      email: user.email,
      first: first.trim(),
      last: last.join(" ").trim(),
    });

    //
    window?.Beacon?.("identify", {
      name: user.full_name,
      email: user.email,
      company: user.organization,
    });

    //
    return user;
  } catch (error) {
    captureSentryExceptionUnless(error, "Failed to load user", [401]);

    throw error;
  }
}

// -----------------------------------------------------------------------------

async function loadOrganization(uuid: string) {
  try {
    return await authenticatedGetFetch(`api/organization/${uuid}`);
  } catch (error) {
    captureSentryExceptionUnless(error, "Failed to load organization", [403]);

    throw error;
  }
}

// -----------------------------------------------------------------------------

async function loadBilling(billingActions: BillingStoreActions) {
  try {
    return await billingActions.load();
  } catch (error) {
    captureSentryExceptionUnless(
      error,
      "Failed to load billing",
      () => window.location.host === "app.threatkey.com"
    );

    throw error;
  }
}

// -----------------------------------------------------------------------------

function isNotAnErrorPage(): boolean {
  return !ErrorPageRegex.test(window.location.pathname);
}
